import { apiClient, uploadFile } from '@/api/client'
import { API_ENDPOINTS } from '@/api/endpoints'
import { ADMIN_CONFIG } from '@/config'

/** 主题主页展示用角色卡，字段均可空；完整度由后端谓词判定。 */
export interface ThemeCharacterCard {
  avatar_url?: string | null
  name?: string | null
  role?: string | null
}

export const THEME_CEFR_LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const
export type ThemeCEFRLevel = typeof THEME_CEFR_LEVELS[number]

export interface ThemeSavePayload {
  code?: string
  title: string
  description?: string | null
  category: string
  cefr_level: ThemeCEFRLevel
  locale: string
  is_active: boolean
  initial_fixed_setting: Record<string, unknown>
  first_episode_title?: string | null
  first_episode_story_text?: string | null
  style_hints?: Record<string, unknown> | null
  theme_specific_prompt?: string | null
  cover_image_url?: string | null
  list_hook?: string | null
  character_cards?: ThemeCharacterCard[]
  /** 写入 / merge 进 themes_info.tags，不是独立列。 */
  tags?: string[] | null
}

function extractUploadUrl(data: unknown): string {
  if (!data || typeof data !== 'object') {
    throw new Error('上传响应无效')
  }

  const payload = data as { url?: unknown; data?: { url?: unknown } }
  if (typeof payload.url === 'string' && payload.url) {
    return payload.url
  }
  if (payload.data && typeof payload.data.url === 'string' && payload.data.url) {
    return payload.data.url
  }

  throw new Error('上传响应缺少 url')
}

/**
 * 把后端返回的相对路径拼成可在后台预览的绝对地址。
 * 保存到主题字段的仍是 /uploads/... 相对 URL。
 */
export function resolveThemeAssetUrl(url?: string | null): string {
  if (!url) {
    return ''
  }
  if (/^(https?:|blob:|data:)/i.test(url)) {
    return url
  }

  const base = (import.meta.env.VITE_API_BASE_URL || ADMIN_CONFIG.API_BASE_URL || '').replace(/\/$/, '')
  const path = url.startsWith('/') ? url : `/${url}`
  return `${base}${path}`
}

export const themesApi = {
  create(payload: ThemeSavePayload) {
    return apiClient.post(API_ENDPOINTS.THEMES.CREATE, payload)
  },

  update(id: number, payload: ThemeSavePayload) {
    return apiClient.put(API_ENDPOINTS.THEMES.UPDATE(id), payload)
  },

  /**
   * 先上传封面拿 URL，再随主题保存写入 cover_image_url。
   * 走现有 uploadFile（multipart field `file`），admin axios 已带管理员 token。
   */
  async uploadCover(file: File): Promise<string> {
    const response = await uploadFile(API_ENDPOINTS.THEMES.UPLOAD_COVER, file)
    return extractUploadUrl(response.data)
  },

  /**
   * 先上传角色头像拿 URL，再随主题保存写入 character_cards[].avatar_url。
   */
  async uploadCharacter(file: File): Promise<string> {
    const response = await uploadFile(API_ENDPOINTS.THEMES.UPLOAD_CHARACTER, file)
    return extractUploadUrl(response.data)
  }
}
